/**
 * Live offline-mirror service: the seam between the REST layer (`api.ts`) and
 * the persistent model (`mirror.ts` + `mirror-store.ts`).
 *
 * Every successful read is written through to the mirror; every read that fails
 * because we are *offline* falls back to the mirror instead of erroring. Compose
 * ops (send/draft) attempted offline are queued in the mirror's outbox and
 * replayed on reconnect. This is what makes reading mail, opening a thread and
 * searching work with no server, and reconcile cleanly when the link returns —
 * without any view needing to know the network is down.
 *
 * Server-side rejections (4xx/5xx, which carry a numeric `status`) are NOT
 * treated as offline — they propagate so the UI surfaces the real error.
 */

import { readActiveAccountSelector } from "@/lib/accounts";
import { generateId } from "@fleet/utils";
import {
  type MailAccountView,
  mailApi,
  type MailFolder,
  type MailThread,
  type MessageDetail,
  type MessageSummary,
  type SendMessageInput,
} from "./api";
import {
  applyFlags,
  dequeueOutbox,
  emptyMirror,
  enqueueOutbox,
  type MailboxMirror,
  outboxEntries,
  type OutboxKind,
  putDetail,
  putFolders,
  putMessages,
  putThreads,
  detail as readDetail,
  folders as readFolders,
  messages as readMessages,
  threads as readThreads,
  removeMessage,
  searchMirror,
} from "./mirror";
import { getMirrorStore, type MirrorStore } from "./mirror-store";

const SAVE_DEBOUNCE_MS = 200;
const ACCOUNTS_KEY_PREFIX = "cartero:mail:accounts:";

/**
 * A failure is "offline" when the browser reports offline, or when the error is
 * a network-layer throw (our `mailFetch` decorates HTTP errors with a numeric
 * `status`; a dropped connection throws a bare `TypeError` with none).
 */
export function isOfflineError(error: unknown): boolean {
  if (typeof navigator !== "undefined" && "onLine" in navigator && !navigator.onLine) {
    return true;
  }
  return error instanceof Error && !("status" in error);
}

function newOutboxId(): string {
  return generateId();
}

export class MailMirror {
  readonly #store: MirrorStore;
  readonly #cache = new Map<string, MailboxMirror>();
  readonly #loading = new Map<string, Promise<MailboxMirror>>();
  readonly #saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  #reconnectBound = false;

  constructor(store: MirrorStore = getMirrorStore()) {
    this.#store = store;
  }

  // ── Internal state plumbing ────────────────────────────────────────────────

  async #ensure(accountId: string): Promise<MailboxMirror> {
    const cached = this.#cache.get(accountId);
    if (cached) {
      return cached;
    }
    let loading = this.#loading.get(accountId);
    if (!loading) {
      loading = this.#store
        .load(accountId)
        .catch(() => null)
        .then((loaded) => {
          const mirror = loaded ?? emptyMirror(accountId);
          this.#cache.set(accountId, mirror);
          this.#loading.delete(accountId);
          return mirror;
        });
      this.#loading.set(accountId, loading);
    }
    return loading;
  }

  #commit(accountId: string, next: MailboxMirror): void {
    this.#cache.set(accountId, { ...next, updatedAt: new Date().toISOString() });
    this.#scheduleSave(accountId);
  }

  #scheduleSave(accountId: string): void {
    const pending = this.#saveTimers.get(accountId);
    if (pending) {
      clearTimeout(pending);
    }
    const timer = setTimeout(() => {
      this.#saveTimers.delete(accountId);
      const mirror = this.#cache.get(accountId);
      if (mirror) {
        void this.#store.save(mirror).catch(() => undefined);
      }
    }, SAVE_DEBOUNCE_MS);
    this.#saveTimers.set(accountId, timer);
  }

  /** Flushes any debounced writes immediately (used by tests and teardown). */
  async flush(accountId?: string): Promise<void> {
    const ids = accountId ? [accountId] : [...this.#saveTimers.keys()];
    for (const id of ids) {
      const timer = this.#saveTimers.get(id);
      if (timer) {
        clearTimeout(timer);
        this.#saveTimers.delete(id);
      }
      const mirror = this.#cache.get(id);
      if (mirror) {
        await this.#store.save(mirror);
      }
    }
  }

  // ── Write-through recorders ────────────────────────────────────────────────

  async recordFolders(accountId: string, folders: MailFolder[]): Promise<void> {
    this.#commit(accountId, putFolders(await this.#ensure(accountId), folders));
  }

  async recordMessages(accountId: string, folder: string, list: MessageSummary[]): Promise<void> {
    this.#commit(accountId, putMessages(await this.#ensure(accountId), folder, list));
  }

  async recordThreads(accountId: string, folder: string, list: MailThread[]): Promise<void> {
    this.#commit(accountId, putThreads(await this.#ensure(accountId), folder, list));
  }

  async recordDetail(accountId: string, message: MessageDetail): Promise<void> {
    this.#commit(accountId, putDetail(await this.#ensure(accountId), message));
  }

  async recordFlags(
    accountId: string,
    folder: string,
    uid: number,
    patch: { seen?: boolean; flagged?: boolean },
  ): Promise<void> {
    this.#commit(accountId, applyFlags(await this.#ensure(accountId), folder, uid, patch));
  }

  async recordMove(accountId: string, folder: string, uid: number): Promise<void> {
    this.#commit(accountId, removeMessage(await this.#ensure(accountId), folder, uid));
  }

  // ── Read wrappers (network → mirror on offline) ────────────────────────────

  async folders(accountId: string): Promise<MailFolder[]> {
    try {
      const data = await mailApi.listFolders(accountId);
      await this.recordFolders(accountId, data);
      return data;
    } catch (error) {
      if (isOfflineError(error)) {
        return readFolders(await this.#ensure(accountId));
      }
      throw error;
    }
  }

  async messages(accountId: string, folder: string): Promise<MessageSummary[]> {
    try {
      const data = await mailApi.listMessages(accountId, folder);
      await this.recordMessages(accountId, folder, data);
      return data;
    } catch (error) {
      if (isOfflineError(error)) {
        return readMessages(await this.#ensure(accountId), folder);
      }
      throw error;
    }
  }

  async threads(accountId: string, folder: string): Promise<MailThread[]> {
    try {
      const data = await mailApi.listThreads(accountId, folder);
      await this.recordThreads(accountId, folder, data);
      return data;
    } catch (error) {
      if (isOfflineError(error)) {
        return readThreads(await this.#ensure(accountId), folder);
      }
      throw error;
    }
  }

  async message(accountId: string, folder: string, uid: number): Promise<MessageDetail> {
    try {
      const data = await mailApi.getMessage(accountId, uid, folder);
      await this.recordDetail(accountId, data);
      return data;
    } catch (error) {
      if (isOfflineError(error)) {
        const cached = readDetail(await this.#ensure(accountId), folder, uid);
        if (cached) {
          return cached;
        }
      }
      throw error;
    }
  }

  async search(accountId: string, query: string): Promise<MessageSummary[]> {
    try {
      return await mailApi.search(accountId, query);
    } catch (error) {
      if (isOfflineError(error)) {
        return searchMirror(await this.#ensure(accountId), query);
      }
      throw error;
    }
  }

  // ── Accounts (device-level list, cached per active selector) ───────────────

  async accounts(): Promise<MailAccountView[]> {
    try {
      const data = await mailApi.listAccounts();
      this.#saveAccounts(data);
      return data;
    } catch (error) {
      if (isOfflineError(error)) {
        return this.#loadAccounts();
      }
      throw error;
    }
  }

  #accountsKey(): string | null {
    const selector = readActiveAccountSelector();
    return selector ? `${ACCOUNTS_KEY_PREFIX}${selector}` : null;
  }

  #saveAccounts(list: MailAccountView[]): void {
    const key = this.#accountsKey();
    if (!key || typeof localStorage === "undefined") {
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch {
      // Storage full/unavailable — mirror stays best-effort.
    }
  }

  #loadAccounts(): MailAccountView[] {
    const key = this.#accountsKey();
    if (!key || typeof localStorage === "undefined") {
      return [];
    }
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return [];
      }
      const parsed: unknown = JSON.parse(raw);
      // Trust boundary: our own serialized accounts list.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      return Array.isArray(parsed) ? (parsed as MailAccountView[]) : [];
    } catch {
      return [];
    }
  }

  // ── Outbox (offline compose → replay on reconnect) ─────────────────────────

  async sendOrQueue(accountId: string, input: SendMessageInput): Promise<{ queued: boolean }> {
    return this.#dispatchOrQueue(accountId, "send", input);
  }

  async draftOrQueue(accountId: string, input: SendMessageInput): Promise<{ queued: boolean }> {
    return this.#dispatchOrQueue(accountId, "draft", input);
  }

  async #dispatchOrQueue(
    accountId: string,
    kind: OutboxKind,
    input: SendMessageInput,
  ): Promise<{ queued: boolean }> {
    try {
      await this.#deliver(accountId, kind, input);
      return { queued: false };
    } catch (error) {
      if (isOfflineError(error)) {
        this.#commit(
          accountId,
          enqueueOutbox(await this.#ensure(accountId), {
            id: newOutboxId(),
            kind,
            input,
            queuedAt: new Date().toISOString(),
          }),
        );
        return { queued: true };
      }
      throw error;
    }
  }

  #deliver(accountId: string, kind: OutboxKind, input: SendMessageInput): Promise<void> {
    return kind === "send" ? mailApi.send(accountId, input) : mailApi.saveDraft(accountId, input);
  }

  async pendingOutboxCount(accountId: string): Promise<number> {
    return outboxEntries(await this.#ensure(accountId)).length;
  }

  /**
   * Replays queued compose ops. Stops on the first still-offline error (keeps
   * the queue for the next reconnect); drops entries the server rejects so a
   * poison message can't wedge the queue forever. Returns the number delivered.
   */
  async drainOutbox(accountId: string): Promise<number> {
    const pending = outboxEntries(await this.#ensure(accountId));
    let delivered = 0;
    for (const entry of pending) {
      try {
        await this.#deliver(accountId, entry.kind, entry.input);
        this.#commit(accountId, dequeueOutbox(await this.#ensure(accountId), entry.id));
        delivered += 1;
      } catch (error) {
        if (isOfflineError(error)) {
          break;
        }
        // Server rejected — drop it rather than retry forever.
        this.#commit(accountId, dequeueOutbox(await this.#ensure(accountId), entry.id));
      }
    }
    return delivered;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /** Drops an account's mirror (on disconnect/delete). */
  async forget(accountId: string): Promise<void> {
    const timer = this.#saveTimers.get(accountId);
    if (timer) {
      clearTimeout(timer);
      this.#saveTimers.delete(accountId);
    }
    this.#cache.delete(accountId);
    this.#loading.delete(accountId);
    await this.#store.remove(accountId).catch(() => undefined);
  }

  /**
   * Registers a one-time `online` listener that drains every cached account's
   * outbox and invokes `onReconciled` so the caller can refetch. Idempotent and
   * a no-op outside the browser.
   */
  ensureReconnectListener(onReconciled?: () => void): void {
    if (this.#reconnectBound || globalThis.window === undefined) {
      return;
    }
    this.#reconnectBound = true;
    globalThis.addEventListener("online", () => {
      const ids = [...this.#cache.keys()];
      void Promise.all(ids.map((id) => this.drainOutbox(id).catch(() => 0))).finally(() => {
        onReconciled?.();
      });
    });
  }
}

/** Process-wide mirror service used by the mail hooks. */
export const mailMirror = new MailMirror();
